import React, {useRef, useState} from 'react';
import {IconButton, InputAdornment, Popover, Typography} from '@mui/material';
import {useLocalization} from 'localization';
import {useShortcut} from 'services/shortcuts';
import {
  ActivityIndicator,
  Layout,
  RemoveIcon,
  SearchIcon,
  TextField,
} from 'components';
import {
  type LookupError,
  useGlobalSearch as useGlobalLookup,
} from '../hooks/useGlobalSearch';

const originBottomLeft = {
  vertical: 'bottom',
  horizontal: 'left',
} as const;

export const GlobalLookup = () => {
  const {lookup: lookup, navigateToResult} = useGlobalLookup();
  const {t} = useLocalization();
  const [lookupText, setLookupText] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState<LookupError | undefined>();
  const lookupButtonRef = useRef<HTMLButtonElement>(null);
  const lookupInputRef = useRef<HTMLInputElement>(null);
  const [anchorElLookup, setAnchorElLookup] =
    useState<HTMLButtonElement | null>(null);
  const lookupIsOpen = Boolean(anchorElLookup);
  const anchorElLookupId = lookupIsOpen ? 'simple-popover' : undefined;
  const handleOpenLookupMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElLookup(event.currentTarget);
  };
  const handleCloseLookupMenu = () => {
    setAnchorElLookup(null);
  };

  useShortcut('globalLookup', () => setAnchorElLookup(lookupButtonRef.current));
  const focusLookupInput = () => lookupInputRef.current?.focus();

  const onLookup = () => {
    const response = lookup(lookupText);
    if (typeof response === 'string') {
      setLookupError(response);
      return;
    }

    setLookupLoading(true);
    response.then(result => {
      setLookupLoading(false);
      if (typeof result === 'string') {
        setLookupError(result);
        return;
      }

      handleCloseLookupMenu();
      navigateToResult(result);
    });
  };

  return (
    <>
      <IconButton
        ref={lookupButtonRef}
        aria-describedby={anchorElLookupId}
        onClick={handleOpenLookupMenu}
        sx={({palette}) => ({
          p: 0.5,
          color: palette.secondary.light,
        })}>
        <SearchIcon />
      </IconButton>
      <Popover
        id={anchorElLookupId}
        open={lookupIsOpen}
        anchorEl={anchorElLookup}
        onClose={() => handleCloseLookupMenu()}
        anchorOrigin={originBottomLeft}
        slotProps={{transition: {onEntered: focusLookupInput}}}>
        <Layout padding={1}>
          <form
            onSubmit={e => {
              e.preventDefault();
              onLookup();
            }}>
            <Typography
              variant={'caption'}
              color={lookupError !== undefined ? 'red' : undefined}>
              {t(`error.search.${lookupError}`)}
            </Typography>
            <TextField
              type={'text'}
              name={'lookup'}
              value={lookupText}
              placeholder={'ISBN/EAN/UUID/URN'}
              onChange={e => {
                setLookupError(undefined);
                setLookupText(e.target.value);
              }}
              layout={{
                width: '500px',
              }}
              inputRef={lookupInputRef}
              autoFocus
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      {lookupText ? (
                        <IconButton
                          size="small"
                          onClick={() => setLookupText('')}>
                          <RemoveIcon />
                        </IconButton>
                      ) : null}
                      <IconButton onClick={() => onLookup()}>
                        {lookupLoading ? (
                          <ActivityIndicator color="inherit" size={'medium'} />
                        ) : (
                          <SearchIcon />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
          </form>
        </Layout>
      </Popover>
    </>
  );
};
